import {
  cloneElement,
  ComponentPropsWithoutRef,
  forwardRef,
  isValidElement,
  useCallback,
} from 'react';
import { Slot } from '@radix-ui/react-slot';

import connectFieldsStyles from '@components/primitives/ConnectedFields/styles.module.css';
import { FocusRing } from '@components/primitives/FocusRing';
import { Spinner } from '@components/primitives/Spinner';
import { cn } from '@components/primitives/utilities';

import css from './styles.module.css';

export interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  /** Use child component as the root element of the button */
  asChild?: boolean;
  /** The size of the button */
  size?: '2xs' | 'xs' | 'sm' | 'md' | 'lg';
  /** Whether the button is busy */
  loading?: boolean;
  /** Color variant */
  variant?: 'primary' | 'secondary' | 'danger' | 'danger-strong';
  /** Whether the button is transparent */
  ghost?: boolean;
  /** Whether to flush the button with its parent container (only applies to ghost buttons) */
  flush?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      asChild,
      className,
      size = 'md',
      loading = false,
      variant = 'secondary',
      ghost = false,
      children,
      disabled,
      flush,
      onClick,
      ...props
    },
    ref
  ) {
    const isDisabled = disabled || loading;

    // Disable default click behavior for all types of elements (that may not respond to :disabled)
    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (isDisabled) {
          e.preventDefault();
          e.stopPropagation();
        } else {
          onClick?.(e);
        }
      },
      [isDisabled, onClick]
    );

    // Check if we can render the child as a Slot
    const canRenderAsChild =
      !!asChild && !!children && isValidElement(children);
    const Component = canRenderAsChild ? Slot : 'button';

    // Override certain props for known use cases (e.g., disabled links)
    const disabledChildProps: Record<string, unknown> = {};
    if (isDisabled && canRenderAsChild && 'href' in children.props) {
      disabledChildProps.href = undefined;
    }

    const content = (
      <>
        {loading && (
          <span className={css.spinner} aria-hidden>
            <Spinner className={css.spinnerIcon} />
          </span>
        )}

        <span className={css.label}>
          {/* If using asChild, render the grandchildren instead of the child */}
          {canRenderAsChild ? children.props.children : children}
        </span>
      </>
    );

    return (
      <FocusRing>
        <Component
          ref={ref}
          className={cn(
            css.button,
            connectFieldsStyles.connectableField,
            className
          )}
          data-size={size}
          data-variant={variant}
          data-ghost={ghost}
          data-flush={!!flush}
          aria-busy={loading}
          aria-disabled={isDisabled}
          disabled={isDisabled}
          onClick={onClick ? handleClick : undefined}
          {...props}
        >
          {/* If rendering asChild, render the button content as grandchildren */}
          {canRenderAsChild
            ? cloneElement(children, disabledChildProps, content)
            : content}
        </Component>
      </FocusRing>
    );
  }
);
